import { memo, useId, useState, type ReactElement, type ReactNode } from "react";

import type { BooleanChoice, BusbarSelectionDraft } from "./busbarSelectionDraft";

/** The first option of every choice: nothing is chosen for the engineer. */
export const CHOOSE = "Choose…";

/** The first option of a yes/no answer: not answering is allowed, and is not "No". */
export const NOT_STATED = "Not stated";

// The document reference of one evidence record or row (founder rule 2026-10-01, follow-up
// 21). The rule itself is the backend's: the form carries the three parts and says what they
// are for.
export const REFERENCE_HINT =
  "All three parts verify these values; anything left blank shows them as Data not verified.";

export const REFERENCE_FIELDS = ["Document", "Revision / date", "Page / clause"] as const;

export type DraftChange = (patch: Partial<BusbarSelectionDraft>) => void;

// What a block receives: its own slice of the draft (typed as exactly the keys it reads, so
// reading a key that is not in the slice does not compile), the one change callback and
// whether the form is disabled.
export type BlockProps<V> = {
  draft: V;
  onChange: DraftChange;
  disabled: boolean;
};

function shallowEqual(left: object, right: object): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(right, key) &&
        (left as Record<string, unknown>)[key] === (right as Record<string, unknown>)[key],
    )
  );
}

// A block that renders again only when a draft key it lists, or one of its other props,
// changed. The draft is one flat object, so without this every keystroke would render every
// block. The slice type of the block is the guard against a key left out of the list: the
// block cannot read what its slice does not name.
export function blockMemo<P extends { draft: object }>(
  Component: (props: P) => ReactElement | null,
  keys: readonly (keyof P["draft"])[],
) {
  return memo(Component, (previous, next) => {
    const { draft: previousDraft, ...previousRest } = previous;
    const { draft: nextDraft, ...nextRest } = next;
    return (
      shallowEqual(previousRest, nextRest) &&
      keys.every((key) => previousDraft[key] === nextDraft[key])
    );
  });
}

type StringKey<T> = {
  [P in keyof T & string]: string extends T[P] ? P : never;
}[keyof T & string];

type BooleanKey<T> = {
  [P in keyof T & string]: string extends T[P] ? never : T[P] extends BooleanChoice ? P : never;
}[keyof T & string];

type TextOptions = { decimal?: boolean; hint?: string };

// The inputs of a block or a row, built over the values it shows and the patch callback it
// reports to. The field name is the draft key (or the row's own name), the hint is tied to the
// input with aria-describedby.
export function fieldKit<T extends object>(
  values: T,
  onChange: (patch: Partial<NoInfer<T>>) => void,
  nameOf: (key: string) => string = (key) => key,
) {
  const read = (key: keyof T) => values[key] as unknown as string;
  const set = (key: keyof T, value: string) => onChange({ [key]: value } as Partial<T>);

  function text(label: string, key: StringKey<T>, options: TextOptions = {}) {
    const name = nameOf(key);
    const hintId = `${name}-hint`;
    return (
      <>
        <label>
          {label}
          <input
            aria-describedby={options.hint === undefined ? undefined : hintId}
            inputMode={options.decimal === true ? "decimal" : "text"}
            name={name}
            value={read(key)}
            onChange={(event) => set(key, event.target.value)}
          />
        </label>
        {options.hint === undefined ? null : <p id={hintId}>{options.hint}</p>}
      </>
    );
  }

  function decimal(label: string, key: StringKey<T>) {
    return text(label, key, { decimal: true });
  }

  // A select with "Choose…" first and the contract's values after it.
  function choice<V extends string>(
    label: string,
    key: StringKey<T>,
    options: readonly V[],
    words: Record<V, string>,
    withBlank = true,
  ) {
    return (
      <label>
        {label}
        <select
          name={nameOf(key)}
          value={read(key)}
          onChange={(event) => set(key, event.target.value)}
        >
          {withBlank ? <option value="">{CHOOSE}</option> : null}
          {options.map((option) => (
            <option key={option} value={option}>
              {words[option]}
            </option>
          ))}
        </select>
      </label>
    );
  }

  // A yes/no answer is a select with "Not stated" first, never a checkbox: a checkbox cannot
  // say "not answered".
  function yesNo(label: string, key: BooleanKey<T>) {
    return (
      <label>
        {label}
        <select
          name={nameOf(key)}
          value={read(key)}
          onChange={(event) => set(key, event.target.value)}
        >
          <option value="">{NOT_STATED}</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </label>
    );
  }

  // The three parts of a document reference, in their own group: the keys are the draft's
  // <prefix>Document, <prefix>RevisionOrDate and <prefix>PageOrClause, or a row's own.
  function reference(
    keys: [StringKey<T>, StringKey<T>, StringKey<T>],
    hint: string = REFERENCE_HINT,
  ) {
    const hintId = `${nameOf(keys[0])}-reference-hint`;
    return (
      <fieldset data-reference-group="true">
        <legend>Reference</legend>
        <p id={hintId}>{hint}</p>
        {REFERENCE_FIELDS.map((label, index) => (
          <label key={label}>
            {label}
            <input
              aria-describedby={hintId}
              name={nameOf(keys[index] as string)}
              value={read(keys[index])}
              onChange={(event) => set(keys[index], event.target.value)}
            />
          </label>
        ))}
      </fieldset>
    );
  }

  return { text, decimal, choice, yesNo, reference };
}

/** True when any of the typed values holds something other than blanks. */
export function anyFilled(...values: readonly string[]): boolean {
  return values.some((value) => value.trim() !== "");
}

type OptionalBlockProps = {
  title: string;
  // Whether the block already holds an entered value: it then starts open, so a restored
  // draft shows what it holds. After that the engineer opens and closes it.
  filled: boolean;
  disabled: boolean;
  children: ReactNode;
};

// An optional block of the study, collapsed until it is needed. Native details/summary: it
// is a keyboard-accessible button for free. The fieldset inside is named by the summary.
export function OptionalBlock({ title, filled, disabled, children }: OptionalBlockProps) {
  const titleId = useId();
  const [open, setOpen] = useState(filled);

  return (
    <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary id={titleId}>{title}</summary>
      <fieldset aria-labelledby={titleId} disabled={disabled}>
        {children}
      </fieldset>
    </details>
  );
}
